import { useState, useRef, useCallback, useEffect, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { NavGroup } from '../../config/navigation';

interface Props {
  group: NavGroup;
  linkColor: string;
  activeLinkColor: string;
  onOpen?: () => void;
}

export function DesktopDropdown({ group, linkColor, activeLinkColor, onOpen }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const hasActiveChild = group.items.some((item) => {
    const path = item.path.startsWith('/') ? item.path : `/${item.path}`;
    return location.pathname === path;
  });

  const triggerColor = hasActiveChild ? activeLinkColor : linkColor;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && onOpen) onOpen();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      const firstItem = ref.current?.querySelector<HTMLElement>('[role="menuitem"]');
      firstItem?.focus();
    }
  };

  const handleItemKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    const items = ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!items) return;
    const current = Array.from(items).indexOf(e.currentTarget as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(current + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(current - 1 + items.length) % items.length]?.focus();
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        className={`text-sm font-semibold transition-colors flex items-center gap-1 ${triggerColor}`}
      >
        {group.label}
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute top-full left-0 mt-2 min-w-[180px] bg-white rounded-lg shadow-elevated border border-slate-200 py-1 animate-fade-in-fast z-50"
        >
          {group.items.map((item) => {
            const path = item.path.startsWith('/') ? item.path : `/${item.path}`;
            const active = location.pathname === path;
            return (
              <Link
                key={item.path}
                to={path}
                role="menuitem"
                tabIndex={-1}
                onClick={close}
                onKeyDown={handleItemKeyDown}
                className={`block px-4 py-2 text-sm transition-colors ${
                  active
                    ? 'text-primary bg-primary/5 font-semibold'
                    : 'text-slate-600 hover:text-primary hover:bg-slate-50'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
